import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OrderVoidReasonCode } from "src/enums/order.enum";
import { formatOrderVoidReasonCode } from "src/helpers/orderVoidDisplay";
import { failureToast } from "src/utils/toasts";
import { PortaledModalFrame } from "src/components/common/PortaledModalFrame";
import {
  orderDrawerPrimaryButtonClass,
  orderDrawerSecondaryButtonClass,
} from "src/constants/orderDrawerButtons";

const VOID_REASON_OPTIONS = Object.values(OrderVoidReasonCode).filter(
  (v): v is OrderVoidReasonCode => typeof v === "string"
);

/** Above order void modal (`z-[100]`) so the menu is not clipped or hidden. */
const REASON_MENU_Z = 200;
const MENU_MIN_WIDTH_PX = 224;
const VIEWPORT_EDGE_PAD = 10;

export interface VoidOrderModalProps {
  open: boolean;
  voidedByDisplay: string | null;
  onConfirm: (reasonCode: OrderVoidReasonCode, note: string | null) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Same fields as eatsbitsdesktop `VoidOrderDialog`. Void reason control matches
 * Employees → Timecards `ReportDateRangePicker` / employee filter (custom menu, not native select).
 */
export function VoidOrderModal({
  open,
  voidedByDisplay,
  onConfirm,
  onCancel,
  isLoading = false,
}: VoidOrderModalProps) {
  const [selectedReason, setSelectedReason] = useState<OrderVoidReasonCode | "">("");
  const [note, setNote] = useState("");
  const [reasonMenuOpen, setReasonMenuOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedReason("");
      setNote("");
      setReasonMenuOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || isLoading) setReasonMenuOpen(false);
  }, [open, isLoading]);

  useLayoutEffect(() => {
    if (!reasonMenuOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const gap = 8;
      const vw = window.innerWidth;
      const width = Math.min(
        Math.max(rect.width, MENU_MIN_WIDTH_PX),
        vw - 2 * VIEWPORT_EDGE_PAD
      );
      let left = rect.left;
      const maxLeft = vw - width - VIEWPORT_EDGE_PAD;
      left = Math.max(VIEWPORT_EDGE_PAD, Math.min(left, maxLeft));

      setDropdownRect({
        top: rect.bottom + gap,
        left,
        width,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [reasonMenuOpen]);

  useEffect(() => {
    if (!reasonMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setReasonMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [reasonMenuOpen]);

  if (!open) return null;

  const noteRequired = selectedReason === OrderVoidReasonCode.OTHER;
  const noteHint = noteRequired ? 'Note (required for "Other"):' : "Note (optional):";

  const handleVoid = () => {
    if (!selectedReason) {
      failureToast("Please select a void reason.");
      return;
    }
    const trimmed = note.trim();
    if (noteRequired && trimmed === "") {
      failureToast("Please enter a note when the void reason is Other.");
      return;
    }
    void onConfirm(selectedReason, trimmed === "" ? null : trimmed);
  };

  const byLine =
    voidedByDisplay != null && voidedByDisplay.trim() !== ""
      ? `Voided by: ${voidedByDisplay.trim()}`
      : "Voided by: (unknown)";

  const reasonTriggerLabel = selectedReason
    ? formatOrderVoidReasonCode(selectedReason)
    : "Select a reason…";

  return (
    <PortaledModalFrame
      onClose={isLoading ? () => {} : onCancel}
      panelClassName="max-w-md"
      panelStyle={{ maxWidth: 420 }}
    >
      <div className="flex min-h-0 max-h-[min(90dvh,720px)] flex-col">
        <div className="shrink-0 border-b border-[#e5e7eb] px-6 py-4">
          <h2 id="modal-title" className="text-lg font-semibold text-[#111827]">
            Void order
          </h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <p className="text-sm text-[#374151]">{byLine}</p>
          <div className="mt-5 space-y-2">
            <div className="text-xs font-semibold text-[#111827]">Void reason:</div>
            <div className="relative text-left" ref={triggerRef}>
              <button
                type="button"
                className="inline-flex w-full min-w-0 items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] disabled:opacity-50 transition-colors"
                aria-expanded={reasonMenuOpen}
                aria-haspopup="true"
                aria-label="Void reason"
                disabled={isLoading}
                onClick={() => {
                  if (isLoading) return;
                  setReasonMenuOpen((o) => !o);
                }}
              >
                <span className="min-w-0 flex-1 truncate text-left">{reasonTriggerLabel}</span>
                <svg
                  className="-mr-1 h-5 w-5 shrink-0 text-[#9ca3af]"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {reasonMenuOpen &&
                dropdownRect != null &&
                createPortal(
                  <div
                    ref={menuRef}
                    className="fixed max-h-[min(320px,50vh)] overflow-y-auto rounded-md border border-[#E5E7EB] bg-white shadow-lg focus:outline-none"
                    style={{
                      top: dropdownRect.top,
                      left: dropdownRect.left,
                      width: dropdownRect.width,
                      zIndex: REASON_MENU_Z,
                    }}
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <div className="py-1" role="none">
                      {VOID_REASON_OPTIONS.map((code) => {
                        const isSelected = selectedReason === code;
                        return (
                          <button
                            key={code}
                            type="button"
                            className={`block w-full px-4 py-2 text-left text-sm ${
                              isSelected
                                ? "bg-[#fef3ef] font-medium text-[#111827]"
                                : "text-[#374151] hover:bg-[#f9fafb]"
                            }`}
                            role="menuitem"
                            onClick={() => {
                              setSelectedReason(code);
                              setReasonMenuOpen(false);
                            }}
                          >
                            {formatOrderVoidReasonCode(code)}
                          </button>
                        );
                      })}
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label htmlFor="void-note" className="text-xs font-semibold text-[#111827]">
              {noteHint}
            </label>
            <textarea
              id="void-note"
              value={note}
              disabled={isLoading}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="w-full resize-y rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#FD7657] focus:ring-1 focus:ring-[#FD7657]/40 disabled:opacity-50"
            />
          </div>
        </div>
        <div className="shrink-0 flex flex-wrap justify-end gap-2 border-t border-[#e5e7eb] px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className={orderDrawerSecondaryButtonClass}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleVoid()}
            disabled={isLoading}
            className={orderDrawerPrimaryButtonClass}
          >
            {isLoading ? "Voiding…" : "Void order"}
          </button>
        </div>
      </div>
    </PortaledModalFrame>
  );
}
